import { useEffect, useState, useRef, useDeferredValue } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { editProvider, fetchProviders, removeProvider } from 'modules/categories/categoriesSlice';
// eslint-disable-next-line max-len
import DoctorConsultationsProvidersComponent from 'modules/categories/doctorConsultations/components/DoctorConsultationsProvidersComponent';
import ConfirmationModal from 'modules/common/components/ConfirmationModal';
import { searchQueryMinLength, status } from 'constants/general';
import { useParams } from 'react-router-dom';
import appModeService from 'services/AppModeService';

// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const DoctorConsultationsProvidersContainer = () => {
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  const dispatch = useDispatch();
  const { priceListId } = useParams();
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  const [searchQuery, setSearchQuery] = useState('');
  const InitialQueryString = {
    priceListId,
    pageNumber: 1,
    pagesize: 10,
  };
  const [providersQueryString, setProvidersQueryString] = useState(InitialQueryString);
  const [isCreateProviderModalHidden, setIsCreateProviderModalHidden] = useState(true);
  const [isDoctorInfoModalHidden, setDoctorInfoModalModalHidden] = useState(true);
  const [filtersSelected, setFiltersSelected] = useState([]);
  const [isDeactivateConfirmationHidden, setIsDeactivateConfirmationHidden] = useState(true);
  const [isExclusiveConfirmationHidden, setIsExclusiveConfirmationHidden] = useState(true);
  const [isRemoveConfirmationHidden, setIsRemoveConfirmationHidden] = useState(true);
  const [pendingAction, setPendingAction] = useState(null);

  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  const submitProvidersBulkState = useSelector(state => state.categories.submitProvidersBulk);
  const removeProviderState = useSelector(state => state.categories.removeProvider);
  const editProviderState = useSelector(state => state.categories.editProvider);

  const prevSubmitProvidersBulk = useDeferredValue(submitProvidersBulkState.status);
  const prevRemoveProviderState = useDeferredValue(removeProviderState.status);
  const prevEditProviderState = useDeferredValue(editProviderState.status);
  useEffect(() => {
    if (
      (status.SUCCESS === submitProvidersBulkState.status &&
        prevSubmitProvidersBulk !== submitProvidersBulkState.status) ||
      (status.SUCCESS === removeProviderState.status &&
        prevRemoveProviderState !== removeProviderState.status) ||
      (status.SUCCESS === editProviderState.status &&
        prevEditProviderState !== editProviderState.status)
    ) {
      setProvidersQueryString({ ...InitialQueryString });
    }
  }, [submitProvidersBulkState.status, removeProviderState.status, editProviderState.status]);
  useEffect(() => {
    dispatch(fetchProviders(providersQueryString));
  }, [dispatch, providersQueryString]);
  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  const searchBarInputTimeout = useRef(null);
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################

  const handleInputFieldChange = () => {};

  const handleSearchInputChange = (searchTerm, reset) => {
    const queryString = {
      ...providersQueryString,
      pageNumber: 1,
      isShowMore: false,
    };
    if (searchTerm) queryString.searchTerm = searchTerm;
    if (reset) delete queryString.searchTerm;

    setProvidersQueryString(queryString);
  };

  const handleSearchBarInputChange = event => {
    const { value } = event.target;

    if (value.length > 30) return;
    setSearchQuery(value);

    if (searchBarInputTimeout.current) clearTimeout(searchBarInputTimeout.current);

    if (value && value.length >= searchQueryMinLength) {
      searchBarInputTimeout.current = setTimeout(() => {
        clearTimeout(searchBarInputTimeout.current);

        handleSearchInputChange(value);
      }, 500);
    } else if (value.length < searchQueryMinLength && providersQueryString.searchTerm)
      handleSearchInputChange(value, true);
  };

  const handleShowMoreClick = () => {
    const data = {
      ...providersQueryString,
    };
    data.pageNumber += 1;
    data.isShowMore = true;
    setProvidersQueryString(data);
  };

  const handleRemoveProvider = rowDataPayload => {
    setPendingAction({ type: 'remove', data: rowDataPayload });
    setIsRemoveConfirmationHidden(false);
  };

  const handleProviderDeactivate = rowDataPayload => {
    setPendingAction({ type: 'deactivate', data: rowDataPayload });
    setIsDeactivateConfirmationHidden(false);
  };

  const handleProviderExclusive = rowDataPayload => {
    setPendingAction({ type: 'exclusive', data: rowDataPayload });
    setIsExclusiveConfirmationHidden(false);
  };

  const confirmDeactivate = () => {
    if (pendingAction?.type === 'deactivate') {
      const { id, name, active, exclusive } = pendingAction.data;
      const payload = {
        id,
        data: {
          priceListId,
          name,
          active: !active,
          exclusive,
        },
      };
      dispatch(editProvider(payload));
    }
    setIsDeactivateConfirmationHidden(true);
    setPendingAction(null);
  };

  const confirmExclusive = () => {
    if (pendingAction?.type === 'exclusive') {
      const { id, exclusive, name, active, spoc } = pendingAction.data;
      const payload = {
        id,
        data: {
          priceListId,
          name,
          active,
          exclusive,
          spoc,
        },
      };
      dispatch(editProvider(payload));
    }
    setIsExclusiveConfirmationHidden(true);
    setPendingAction(null);
  };

  const confirmRemove = () => {
    if (pendingAction?.type === 'remove') {
      const { id, priceListId } = pendingAction.data;
      const payload = {
        id,
        data: {
          priceListId,
        },
      };
      dispatch(removeProvider(payload));
    }
    setIsRemoveConfirmationHidden(true);
    setPendingAction(null);
  };

  const cancelConfirmation = () => {
    setIsDeactivateConfirmationHidden(true);
    setIsExclusiveConfirmationHidden(true);
    setIsRemoveConfirmationHidden(true);
    setPendingAction(null);
  };

  const handleDropDownFilterChange = (fieldName, selectedValue) => {
    if (selectedValue) {
      setProvidersQueryString(prevValue => {
        const fieldsObj = { ...prevValue };
        fieldsObj[fieldName] = selectedValue;
        return fieldsObj;
      });
      setFiltersSelected(prevState => ({
        ...prevState,
        [fieldName]: true,
      }));
    } else {
      setProvidersQueryString(prevValue => {
        const fieldsObj = { ...prevValue };
        delete fieldsObj[fieldName];
        return fieldsObj;
      });
      setFiltersSelected(prevState => {
        const tempState = { ...prevState };
        delete tempState[fieldName];
        return tempState;
      });
    }
  };
  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################
  return (
    <>
      <DoctorConsultationsProvidersComponent
        isCreateProviderModalHidden={isCreateProviderModalHidden}
        setIsCreateProviderModalHidden={setIsCreateProviderModalHidden}
        isDoctorInfoModalHidden={isDoctorInfoModalHidden}
        setDoctorInfoModalModalHidden={setDoctorInfoModalModalHidden}
        handleInputFieldChange={handleInputFieldChange}
        filtersSelected={filtersSelected}
        searchQuery={searchQuery}
        handleSearchBarInputChange={handleSearchBarInputChange}
        providersQueryString={providersQueryString}
        handleShowMoreClick={handleShowMoreClick}
        handleRemoveProvider={handleRemoveProvider}
        handleProviderDeactivate={handleProviderDeactivate}
        handleProviderExclusive={handleProviderExclusive}
        priceListId={priceListId}
        handleDropDownFilterChange={handleDropDownFilterChange}
      />

      {/* Deactivate Confirmation Modal */}
      <ConfirmationModal
        isModalHidden={isDeactivateConfirmationHidden}
        onCloseModal={cancelConfirmation}
        onConfirm={confirmDeactivate}
        title={pendingAction?.data?.active ? 'Deactivate Provider' : 'Activate Provider'}
        message={
          pendingAction?.data?.active
            ? `Are you sure you want to deactivate ${pendingAction?.data?.provider?.providerName}?`
            : `Are you sure you want to activate ${pendingAction?.data?.provider?.providerName}?`
        }
        confirmText={pendingAction?.data?.active ? 'Deactivate' : 'Activate'}
        confirmButtonColor={pendingAction?.data?.active ? '#DC3545' : '#28A745'}
        isLoading={editProviderState.status === status.FETCHING}
      />

      {/* Exclusive Toggle Confirmation Modal */}
      <ConfirmationModal
        isModalHidden={isExclusiveConfirmationHidden}
        onCloseModal={cancelConfirmation}
        onConfirm={confirmExclusive}
        title={
          appModeService.isEmbedded
            ? pendingAction?.data?.spoc
              ? 'Set as Spoke'
              : 'Remove Spoke Status'
            : pendingAction?.data?.exclusive
            ? 'Set as Exclusive'
            : 'Remove Exclusive Status'
        }
        message={
          appModeService.isEmbedded
            ? pendingAction?.data?.spoc
              ? `Are you sure you want to set ${pendingAction?.data?.provider?.providerName} as spoke?`
              : `Are you sure you want to remove ${pendingAction?.data?.provider?.providerName} from being spoke?`
            : pendingAction?.data?.exclusive
            ? `Are you sure you want to set ${pendingAction?.data?.provider?.providerName} as exclusive?`
            : `Are you sure you want to remove ${pendingAction?.data?.provider?.providerName} from being exclusive?`
        }
        confirmText="Confirm"
        confirmButtonColor="#0070CD"
        isLoading={editProviderState.status === status.FETCHING}
      />

      {/* Remove Provider Confirmation Modal */}
      <ConfirmationModal
        isModalHidden={isRemoveConfirmationHidden}
        onCloseModal={cancelConfirmation}
        onConfirm={confirmRemove}
        title="Remove Provider"
        message={
          pendingAction?.data?.provider?.providerName
            ? `Are you sure you want to remove ${pendingAction?.data?.provider?.providerName} from the list?`
            : 'Are you sure you want to remove this provider from the list?'
        }
        confirmText="Remove"
        cancelText="Cancel"
        confirmButtonColor="#DC3545"
        isLoading={removeProviderState.status === status.FETCHING}
      />
    </>
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

DoctorConsultationsProvidersContainer.propTypes = {};

DoctorConsultationsProvidersContainer.defaultProps = {};

export default DoctorConsultationsProvidersContainer;
